import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, input, OnInit, OnDestroy, inject, Inject } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { MatChipsModule } from '@angular/material/chips';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatDividerModule } from '@angular/material/divider';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Subject, takeUntil, combineLatest, firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { userDataService } from 'src/app/services/userDataService';
import { TelnyxWebRTCService } from 'src/app/services/telnyx-webrtc.service';
import { CallFacadeService } from 'src/app/services/call';
import { CallState, CallSession, CallDisposition } from 'src/types/telnyx-webrtc';
import { AudioPlayerDialogComponent } from './dialog-autoPlay';
import { CustomDatePipe } from 'src/app/pipe/date-format.pipe';

@Component({
  selector: 'app-calls',
  standalone: true,
  imports: [
    CommonModule,
    MatIcon,
    MatTableModule,
    MatButtonModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatSnackBarModule,
    MatChipsModule,
    MatExpansionModule,
    MatDividerModule,
    MatDialogModule,
    CustomDatePipe
  ],
  templateUrl: './calls.component.html',
  styleUrl: './calls.component.scss'
})
export class CallsComponent implements OnInit, OnDestroy {
  data = input<any>();
  params = input<any>();
  lead = input<any>();
  isModal = input<boolean>(false);  // Using the new Angular 16+ input() syntax
  
  // Existing properties
  currentUser: any;
  paramsChild: any;
  total: any;
  currentLead: any;
  calls: any[] = [];
  fullNameCurrentUser: string;
  
  // WebRTC and call state properties
  private destroy$ = new Subject<void>();
  private telnyxService = inject(TelnyxWebRTCService);
  private callFacade = inject(CallFacadeService);
  
  // Call state tracking
  currentCallState: CallState = 'idle';
  activeCall: CallSession | null = null;
  isWebRTCConnected = false;
  isProcessing = false;
  lastError: string | null = null;
  
  // Call history
  isLoadingHistory = false;
  
  // UI state
  showCallControls = false;
  showLoadMore = true;
  isLoadingMore = false;
  
  // Track audio player dialog
  isPlayingAudio = false;
  
  // Table columns for call history
  displayedColumns: string[] = ['phoneNumber', 'start_time', 'duration', 'waitTime', 'agent', 'call_status', 'actions'];

  constructor(
    private readonly userDataService: userDataService,
    private http: HttpClient,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {}

  async ngOnInit() {
    this.currentUser = await this.userDataService.getUserData();
    this.paramsChild = this.params();
    this.calls = this.data().data;
    this.total = this.data().total;
    this.currentLead = this.lead();
    this.fullNameCurrentUser =
      this.currentLead.user?.name + ' ' + this.currentLead.user?.lastName;
    
    // Check if we need to show "Load More" button
    if (this.paramsChild.pageIndex * this.paramsChild.pageSize >= this.total) {
      this.showLoadMore = false;
    }

    // Initialize call functionality
    this.setupCallSubscriptions();
  }
  
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Setup reactive subscriptions for call state management
   */
  private setupCallSubscriptions(): void {
    // Monitor WebRTC connection status (signal)
    const checkConnection = () => {
      this.isWebRTCConnected = this.telnyxService.connected();
    };
    
    // Initial check and periodic monitoring
    checkConnection();
    const connectionInterval = setInterval(checkConnection, 1000);

    // Monitor call state changes (signals)
    const checkCallState = () => {
      this.currentCallState = this.callFacade.currentState();
      this.activeCall = this.callFacade.activeCall();
      this.isProcessing = this.callFacade.isProcessing();
      this.showCallControls = this.isCallActive;
    };
    
    // Initial check and periodic monitoring
    checkCallState();
    const stateInterval = setInterval(checkCallState, 500);

    // Monitor call completion for history refresh
    this.callFacade.callEnded$
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ session, disposition }) => {
        // Refresh call history and stats when call ends
        this.refreshCallData();
      });

    // Monitor call errors
    this.callFacade.callErrors$
      .pipe(takeUntil(this.destroy$))
      .subscribe(error => {
        this.lastError = error.message;
        console.error('Call error in leads calls tab:', error);
      });
      
    // Cleanup intervals
    this.destroy$.subscribe(() => {
      clearInterval(connectionInterval);
      clearInterval(stateInterval);
    });
  }

  /**
   * Load more calls when user clicks "Load More"
   */
  async loadMoreCalls(): Promise<void> {
    if (this.isLoadingMore) return;
    
    try {
      this.isLoadingMore = true;
      
      // Increment the page index
      this.paramsChild.pageIndex++;
      
      // Check if we'll reach the end after this request
      if (this.paramsChild.pageIndex * this.paramsChild.pageSize >= this.total) {
        this.showLoadMore = false;
      }
      
      // Make HTTP request to load more calls
      const url = `${environment.apiUrl}/calls`;
      const response = await firstValueFrom(
        this.http.get<any>(url, { params: this.paramsChild })
      );
      
      // Append new data to existing calls
      if (response && response.data) {
        // The issue is likely here - ensure we're properly appending the array
        this.calls = [...this.calls, ...response.data];
      }
    } catch (error) {
      console.error('Failed to load more calls:', error);
      this.lastError = 'Failed to load more calls';
    } finally {
      this.isLoadingMore = false;
    }
  }

  /**
   * Format call duration for display
   */
  formatDuration(seconds?: number): string {
    if (!seconds) return '00:00';
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  /**
 * Format wait/ring time for display
 */
formatWaitTime(call: any): string {
  // For inbound calls, use queue_wait_time if available, otherwise ring_duration
  // For outbound calls, use ring_duration
  const waitSeconds = call.queue_wait_time || call.ring_duration;
  
  if (!waitSeconds) return '-';
  
  if (waitSeconds < 60) {
    return `${waitSeconds}s`;
  }
  
  const minutes = Math.floor(waitSeconds / 60);
  const seconds = waitSeconds % 60;
  return `${minutes}m ${seconds}s`;
}

/**
 * Get agent display name for the call
 */
getAgentName(call: any): string {
  // For inbound calls that were answered, show the agent who answered
  const agent = call.answeredByAgent || call.user;
  
  if (!agent) return '-';
  
  return `${agent.name || ''} ${agent.lastName || ''}`.trim() || '-';
}

  /**
   * Get call status display color
   */
  getCallStatusColor(status: string): string {
    switch (status) {
      case 'completed': return 'primary';
      case 'no-answer': 
      case 'cancelled': return 'warn';
      case 'busy': return 'accent';
      case 'failed': return 'warn';
      default: return '';
    }
  }

  /**
   * Get call direction icon
   */
  getDirectionIcon(direction: 'inbound' | 'outbound' | 'incoming' | 'outgoing'): string {
    return (direction === 'inbound' || direction === 'incoming') ? 'call_received' : 'call_made';
  }

  /**
   * Check if currently in an active call
   */
  get isCallActive(): boolean {
    return ['dialing', 'ringing', 'connecting', 'connected', 'held'].includes(this.currentCallState);
  }

  /**
   * Get primary phone number for the lead
   */
  get leadPhoneNumber(): string | undefined {
    // Try to get phone from different possible locations
    return this.currentLead?.phone || 
           this.currentLead?.phoneNumber || 
           this.currentLead?.primaryPhone || 
           this.currentLead?.contact?.phone;
  }

  /**
   * Get lead display name
   */
  get leadDisplayName(): string {
    const firstName = this.currentLead?.firstName || this.currentLead?.name || '';
    const lastName = this.currentLead?.lastName || '';
    return `${firstName} ${lastName}`.trim() || 'Unknown Lead';
  }

  /**
   * Refresh call data - reset pagination and reload data
   */
  async refreshCallData(): Promise<void> {
    try {
      this.isLoadingHistory = true;
      
      // Reset pagination to first page
      this.paramsChild.pageIndex = 1;
      
      // Make HTTP request to reload calls
      const url = `${environment.apiUrl}/calls`;
      const response = await firstValueFrom(
        this.http.get<any>(url, { params: this.paramsChild })
      );
      
      if (response) {
        this.calls = response.data;
        this.total = response.total;
        
        // Reset showLoadMore based on new total
        this.showLoadMore = this.paramsChild.pageIndex * this.paramsChild.pageSize < this.total;
      }
      
    } catch (error) {
      console.error('Failed to refresh call data:', error);
      this.lastError = 'Failed to refresh call data';
    } finally {
      this.isLoadingHistory = false;
    }
  }

  /**
   * Track function for call history table
   */
  trackById(index: number, call: any): any {
    return call.id || index;
  }

  /**
   * Check if a call is recent (within the last 24 hours)
   */
  isRecentCall(callDate: string): boolean {
    const oneDayAgo = new Date();
    oneDayAgo.setDate(oneDayAgo.getDate() - 1);
    return new Date(callDate) > oneDayAgo;
  }

  /**
   * Get signed URL for recording
   */
  async getRecordingSignedUrl(call: any): Promise<string> {
    if (!call.recording || !call.recording.s3_key) {
      return '';
    }
    
    try {
      // Get signed URL for the recording
      const response = await firstValueFrom(
        this.http.get<any>(`${environment.apiUrl}/aws/signUrlNoPublic`, {
          params: {
            key: call.recording.s3_key,
            expSeconds: '3600' // 1 hour expiration
          }
        })
      );
      
      return response?.signUrl || '';
    } catch (error) {
      console.error('Failed to get signed URL for recording:', error);
      this.snackBar.open('Failed to get recording URL', 'Dismiss', {
        duration: 3000,
        panelClass: 'error-snackbar'
      });
      return '';
    }
  }

  /**
   * Play recording in dialog
   */
  async playRecording(call: any): Promise<void> {
    if (!call.recording || !call.recording.s3_key) {
      this.snackBar.open('No recording available for this call', 'Dismiss', {
        duration: 3000
      });
      return;
    }
    
    this.isPlayingAudio = true;
    
    try {
      const signedUrl = await this.getRecordingSignedUrl(call);
      
      if (!signedUrl) {
        throw new Error('Could not get recording URL');
      }
      
      // Open dialog with audio player
      const dialogRef = this.dialog.open(AudioPlayerDialogComponent, {
        width: '500px',
        data: {
          audioUrl: signedUrl,
          call: call,
          title: `Call Recording - ${new Date(call.start_time).toLocaleString()}`
        }
      });
      
      // Wait for dialog to close
      await dialogRef.afterClosed().toPromise();
    } catch (error) {
      console.error('Error playing recording:', error);
      this.snackBar.open('Failed to play recording', 'Dismiss', {
        duration: 3000,
        panelClass: 'error-snackbar'
      });
    } finally {
      this.isPlayingAudio = false;
    }
  }

  /**
   * Download recording
   */
  async downloadRecording(call: any): Promise<void> {
    if (!call.recording || !call.recording.s3_key) {
      this.snackBar.open('No recording available for this call', 'Dismiss', {
        duration: 3000
      });
      return;
    }
    
    try {
      const signedUrl = await this.getRecordingSignedUrl(call);
      
      if (!signedUrl) {
        throw new Error('Could not get recording URL');
      }
      
      // Create download element
      const a = document.createElement('a');
      a.href = signedUrl;
      a.download = `Call_${call.id}_${new Date(call.start_time).toLocaleDateString().replace(/\//g, '-')}.mp3`;
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      this.snackBar.open('Downloading recording...', 'Dismiss', {
        duration: 3000
      });
    } catch (error) {
      console.error('Error downloading recording:', error);
      this.snackBar.open('Failed to download recording', 'Dismiss', {
        duration: 3000,
        panelClass: 'error-snackbar'
      });
    }
  }

  /**
   * Check if call has recording
   */
  hasRecording(call: any): boolean {
    return call.recording && call.recording.s3_key;
  }
}